import {useMemo, useState} from 'react'
import {useQuery} from '@tanstack/react-query'
import {CheckCircle, RefreshCw} from 'lucide-react'
import {usePageTitle} from '@/hooks/use-page-title'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {Icon} from '@/components/shared/Icon'
import {EntityLink as EntityLinkChip} from '@/components/shared/EntityLink'
import {MutedText} from '@/components/shared/MutedText'
import {Stack} from '@/components/shared/Stack'
import {StatusText} from '@/components/shared/StatusText'
import {TruncatedCell} from '@/components/shared/TruncatedCell'
import {TableEmptyState} from '@/components/shared/TableEmptyState'
import {PageLayout} from '@/components/shared/PageLayout'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {CONCEPT_ICON} from '@/lib/concepts'
import {Badge} from '@/components/ui/badge'
import {Button} from '@/components/ui/button'
import {Pagination} from '@/components/ui/pagination'
import {useServerPagination, SortDir} from '@/hooks/use-server-pagination'
import {UnifiedSearchBar} from '@/components/shared/UnifiedSearchBar'
import {ProvisioningTaskControllerSearchHistoryEndpoint} from '@/types/endpoint-contracts'
import {formatDate, formatRelativeTime} from '@/lib/date-utils'
import {ProvisioningAction} from '@/types/provisioning'
import type {ProvisioningAction as ProvisioningActionType} from '@/types/provisioning'

// WHY: spec §4.8.3 — audit-class minimum-q threshold. The backend rejects
// shorter values with HTTP 400 + VA0001_MIN_LENGTH; the bar enforces the
// same client-side so the user gets instant feedback instead of an error toast.
const HISTORY_Q_MIN_CHARS = 3

/**
 * Provisioning History list page — Phase C.6 unified search migration
 * (issue 342.11). The page renders {@code ProvisioningTaskHistory} rows
 * (audit-class entity, spec §8.5) — HUGE volume, only the human-readable
 * display-name fields participate in {@code q}, and the diagnostic
 * {@code error} column is intentionally NOT searched. The bar enforces
 * the 3-character minimum spec §4.8.3 client-side; the inline scope
 * hint explains the search surface to the operator.
 */
export function ProvisioningHistory() {
    const services = useServices()
    const i18n = useI18n()
    usePageTitle(i18n.provisioning.historyPageTitle)

    const {
        page, pageSize, queryString,
        setPage, setPageSize, pageSizeOptions,
        searchAndFilters, setSearchAndFilters,
    } = useServerPagination({
        endpoint: ProvisioningTaskControllerSearchHistoryEndpoint,
        defaultSortBy: ProvisioningTaskControllerSearchHistoryEndpoint.sort.COMPLETED_AT,
        defaultSortDir: SortDir.DESC,
    })

    const [showMinQHint, setShowMinQHint] = useState(false)

    const hasActiveFilters = useMemo(() => {
        return !!searchAndFilters.q
            || Object.keys(searchAndFilters.filters).length > 0
            || Object.keys(searchAndFilters.dynamicAttributes).length > 0
    }, [searchAndFilters])

    const {data, isLoading, error, refetch} = useQuery({
        queryKey: ['provisioning-history', queryString],
        queryFn: () => services.provisioning.history(Object.fromEntries(new URLSearchParams(queryString))),
    })

    const totalItems = data?.totalElements || 0
    const totalPages = data?.totalPages || 0

    const getActionBadge = (action: ProvisioningActionType) => {
        switch (action) {
            case ProvisioningAction.GRANT:
                return <Badge variant="success">{i18n.provisioning.actionGrant}</Badge>
            case ProvisioningAction.REVOKE:
                return <Badge variant="warning">{i18n.provisioning.actionRevoke}</Badge>
            case ProvisioningAction.CREATE_ACCOUNT:
                return <Badge variant="success">{i18n.provisioning.actionCreateAccount}</Badge>
            case ProvisioningAction.ENABLE_ACCOUNT:
                return <Badge variant="success">{i18n.provisioning.actionEnableAccount}</Badge>
            case ProvisioningAction.DISABLE_ACCOUNT:
                return <Badge variant="warning">{i18n.provisioning.actionDisableAccount}</Badge>
            case ProvisioningAction.DELETE_ACCOUNT:
                return <Badge variant="destructive">{i18n.provisioning.actionDeleteAccount}</Badge>
            case ProvisioningAction.UPDATE_ACCOUNT:
                return <Badge variant="secondary">{i18n.provisioning.actionUpdateAccount}</Badge>
        }
    }

    return (
        <PageLayout
            data-testid="provisioning-history-page"
            variant="list"
            icon={CONCEPT_ICON.provisioningHistory}
            loading={isLoading}
            error={error}
            title={i18n.provisioning.allHistory}
            count={totalItems}
            description={`${i18n.provisioning.completedTaskCount({count: totalItems})}${hasActiveFilters ? ` ${i18n.provisioning.matchingFilters}` : ''}`}
            pagination={totalItems > 0 ? (
                <Pagination
                    page={page}
                    pageSize={pageSize}
                    totalItems={totalItems}
                    totalPages={totalPages}
                    onPageChange={setPage}
                    onPageSizeChange={setPageSize}
                    pageSizeOptions={pageSizeOptions}
                />
            ) : undefined}
            filters={
                <Stack gap="xs" className="w-full">
                    <UnifiedSearchBar
                        contract={ProvisioningTaskControllerSearchHistoryEndpoint}
                        value={searchAndFilters}
                        onChange={setSearchAndFilters}
                        placeholder={i18n.provisioning.historySearchPlaceholder as string}
                        qMinChars={HISTORY_Q_MIN_CHARS}
                        onMinQHintChange={setShowMinQHint}
                    />
                    {showMinQHint && (
                        <StatusText
                            tone="warning"
                            size="xs"
                            data-testid="history-min-q-hint"
                        >
                            {i18n.provisioning.historyMinQHint}
                        </StatusText>
                    )}
                    <StatusText
                        tone="info"
                        size="xs"
                        muted
                        data-testid="history-search-scope-hint"
                    >
                        {i18n.provisioning.historySearchScopeHint}
                    </StatusText>
                    <Button data-testid="btn-refresh" variant="outline" size="sm" onClick={() => refetch()}>
                        <RefreshCw className="h-4 w-4"/>
                    </Button>
                </Stack>
            }
        >
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead data-testid="col-identity">{i18n.provisioning.identity}</TableHead>
                        <TableHead data-testid="col-entitlement">{i18n.provisioning.entitlement}</TableHead>
                        <TableHead data-testid="col-target-system">{i18n.provisioning.targetSystem}</TableHead>
                        <TableHead data-testid="col-action">{i18n.provisioning.action}</TableHead>
                        <TableHead data-testid="col-status">{i18n.common.status}</TableHead>
                        <TableHead data-testid="col-created-at">{i18n.provisioning.createdAt}</TableHead>
                        <TableHead data-testid="col-completed-at">{i18n.provisioning.completedAt}</TableHead>
                        <TableHead data-testid="col-retries">{i18n.provisioning.retries}</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {data?.content.map((task) => (
                        <TableRow key={task.id}>
                            <TableCell>
                                <EntityLinkChip
                                    value={task.identity}
                                    testId={`link-identity-detail-${task.identity.id}`}
                                />
                            </TableCell>
                            <TruncatedCell
                                maxWidth="200px"
                                title={task.enforcementObject?.displayName || '\u2014'}
                            >
                                {task.enforcementObject ? (
                                    <EntityLinkChip value={task.enforcementObject}/>
                                ) : '\u2014'}
                            </TruncatedCell>
                            <TableCell>
                                <EntityLinkChip value={task.targetSystem}/>
                            </TableCell>
                            <TableCell>{getActionBadge(task.action)}</TableCell>
                            <TableCell>
                                <Badge variant="success">
                                    <Icon icon={CheckCircle} size="xs" className="mr-1"/>
                                    {i18n.provisioning.done}
                                </Badge>
                            </TableCell>
                            <TableCell>
                                <MutedText size="sm">
                                    {formatDate(task.createdAt)}
                                </MutedText>
                            </TableCell>
                            <TableCell>
                                <MutedText size="sm">
                                    {formatDate(task.completedAt)}
                                </MutedText>
                                <MutedText size="xs">
                                    {formatRelativeTime(task.completedAt)}
                                </MutedText>
                            </TableCell>
                            <TableCell>
                                <MutedText size="sm">{task.retryCount}/{task.maxRetries}</MutedText>
                            </TableCell>
                        </TableRow>
                    ))}
                    {data?.content.length === 0 && (
                        <TableEmptyState
                            colSpan={8}
                            hasFilters={hasActiveFilters}
                            matchMessage={i18n.provisioning.noHistoryMatch}
                            emptyMessage={i18n.provisioning.noHistoryFound}
                        />
                    )}
                </TableBody>
            </Table>
        </PageLayout>
    )
}
